import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/primitives/dialog";
import {
  accountDeletedLocalTeardown,
  deleteOwnUserAccount,
} from "@/middleware/accountDeletion";
import { getOwnedGroupChatsFromLocalDB } from "@/indexedDB/services/chats";
import { ApiError, AppError } from "@/server/apiError";
import { useAuthState } from "@/zustand/AuthState";

type BlockedGroup = { id: string; name: string };

type Phase = "confirm" | "pending" | "blocked" | "unavailable" | "error";

/**
 * Account deletion v2 — `DELETE /umbra/v2/users/me` behind
 * featureFlags.accountDeletionV2 (see docs/account-deletion.md).
 *
 * A CONTROLLED dialog, unlike the legacy flow whose confirm button sat inside
 * DialogClose and closed the dialog while the request was still in flight —
 * users read that as success even when the delete was refused. Here the
 * dialog closes only on success; every outcome renders inline:
 *
 * - 204 (and 404 Account.NotFound — already gone) → success handled in the
 *   middleware, then redirect to /login with replace.
 * - 409 Account.OwnedGroupsExist → blocked state with the groups from
 *   `extensions.ownedGroups` as tappable rows. This is the NORMAL path for
 *   any user who owns a group, not an edge case — a local pre-flight shows
 *   the same list before the destructive click.
 * - 400 Account.NotEnabled → non-destructive "temporarily unavailable".
 * - 500 Account.DeleteFailed → inline error with exactly one retry (the
 *   backend marks it safe to retry once).
 * - Auth wiped mid-attempt (interceptor refresh failure) → the session is
 *   over either way; clean up locally and go to login. Never auto-retry.
 */
export default function DeleteAccountV2() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("confirm");
  const [blockedGroups, setBlockedGroups] = useState<BlockedGroup[]>([]);
  const [ownedLocal, setOwnedLocal] = useState<BlockedGroup[]>([]);
  const [errorText, setErrorText] = useState("");
  const [retryUsed, setRetryUsed] = useState(false);

  // Advisory pre-flight: owned groups from the local DB, shown BEFORE the
  // destructive click so the 409 becomes a backstop rather than the primary
  // UX. The button stays enabled — a fresh install has no local list and the
  // server's 409 is authoritative.
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    getOwnedGroupChatsFromLocalDB()
      .then((chats) => {
        if (!cancelled) {
          setOwnedLocal(chats.map((c) => ({ id: c.id, name: c.name })));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    // Never close mid-flight — a vanishing dialog reads as success.
    if (!next && phase === "pending") {
      return;
    }
    setOpen(next);
    if (!next) {
      setPhase("confirm");
      setBlockedGroups([]);
      setErrorText("");
      setRetryUsed(false);
    }
  };

  const goToGroup = (chatId: string) => {
    setOpen(false);
    navigate(`/${chatId}`);
  };

  const handleConfirm = async () => {
    setPhase("pending");
    try {
      await deleteOwnUserAccount();
      setOpen(false);
      navigate("/login", { replace: true });
    } catch (error) {
      // Auth got wiped during the attempt (401 → failed token refresh). The
      // session is terminal either way — and the most likely way to get HERE
      // is that a previous DELETE actually landed (lost 204 → retry → 401),
      // so run the full DELETION teardown, not a plain logout: decrypted
      // OPFS plaintext, caches and push keys must not survive a deleted
      // account. Safe on the false-positive branch too (ordinary session
      // expiry) — the teardown is a strict superset of logout and the data
      // re-syncs on next login. Never retry a DELETE on a 401-derived error.
      if (!useAuthState.getState().tk) {
        await accountDeletedLocalTeardown();
        setOpen(false);
        navigate("/login", { replace: true });
        return;
      }

      if (error instanceof ApiError) {
        const data = error.data as AppError | undefined;
        const code = data?.title;
        if (error.status === 409) {
          setBlockedGroups(data?.extensions?.ownedGroups ?? []);
          setPhase("blocked");
          return;
        }
        if (code === "errors.Account.NotEnabled") {
          setPhase("unavailable");
          return;
        }
        // 500 Account.DeleteFailed and anything unmapped: never render raw
        // server prose — resolve the code against our locale, fall back to
        // the generic error.
        setErrorText(t([code ?? "errorOccurred", "errorOccurred"]));
        setPhase("error");
        return;
      }

      setErrorText(t("errorOccurred"));
      setPhase("error");
    }
  };

  const groupList = blockedGroups.length > 0 ? blockedGroups : ownedLocal;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        data-testid="delete-account-trigger"
        className="m-3 text-destructive"
      >
        {t("account.delete")}
      </DialogTrigger>
      <DialogContent>
        {phase === "blocked" ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("account.deleteBlockedTitle")}</DialogTitle>
              <DialogDescription>
                {t("account.deleteBlockedDescription")}
              </DialogDescription>
            </DialogHeader>
            {groupList.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {groupList.map((group) => (
                  <li key={group.id}>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => goToGroup(group.id)}
                    >
                      {group.name}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              // 409 without extensions.ownedGroups AND no local list (fresh
              // install, pre-sync): without this the blocked state is a dead
              // end — a description ending in ":" above an empty list.
              <p className="text-sm text-muted-foreground">
                {t("errors.Account.OwnedGroupsExist")}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("close")}
              </Button>
            </DialogFooter>
          </>
        ) : phase === "unavailable" ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("account.delete")}</DialogTitle>
              <DialogDescription>
                {t("errors.Account.NotEnabled")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("close")}
              </Button>
            </DialogFooter>
          </>
        ) : phase === "error" ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("account.deleteFailedTitle")}</DialogTitle>
              <DialogDescription>{errorText}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t("close")}
              </Button>
              {!retryUsed && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setRetryUsed(true);
                    void handleConfirm();
                  }}
                >
                  {t("account.deleteRetry")}
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("areYouSure.absolutelySure")}</DialogTitle>
              <DialogDescription>
                {t("account.deleteDescription")}
              </DialogDescription>
            </DialogHeader>
            {ownedLocal.length > 0 && (
              <div className="text-sm text-muted-foreground">
                <p className="mb-1 font-medium text-foreground">
                  {t("account.deleteBlockedTitle")}
                </p>
                <p>{t("account.deleteBlockedDescription")}</p>
                <ul className="mt-1 flex flex-col gap-1">
                  {ownedLocal.map((group) => (
                    <li key={group.id}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => goToGroup(group.id)}
                      >
                        {group.name}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={phase === "pending"}
                onClick={() => handleOpenChange(false)}
              >
                {t("close")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                data-testid="delete-account-confirm"
                aria-label={t("delete")}
                disabled={phase === "pending"}
                onClick={() => void handleConfirm()}
              >
                {phase === "pending" ? t("account.deletePending") : t("delete")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
